"use client";

// Ported from relay-protocol packages/relay-widget/src/components/Dropdown.tsx
// (v0.1.0). Mechanical substitution only: CSS Modules `styles.x` references
// become literal "rd-x" class names (see ../styles/component.css's header
// mapping table) and the CSS Modules import becomes the plain side-effect
// import below. One addition beyond the mechanical rename: `data-vaul-no-drag`
// on the listbox <ul>, so a vaul drawer host (e.g. RelayDeposit's mobile
// ResponsiveShell) doesn't mistake a pointer drag over the open popup for a
// drawer-dismiss swipe (see WalletInfoContent.tsx's `wi-scrollable-body` for
// the existing convention this follows). Everything else -- the ARIA
// "Collapsible Dropdown Listbox" pattern, keyboard handling, outside-pointerdown
// close, and the option-click preventDefault() guard against label-forwarded
// re-toggling -- is the original logic, untouched.
//
// Accessible custom dropdown used in place of a native <select> wherever an
// option needs a leading icon (native <select>/<option> can't render
// per-option images/SVGs in most browsers). Implements the ARIA
// "Collapsible Dropdown Listbox" pattern: a single-select popup button whose
// focus never leaves the trigger -- the currently-highlighted option is
// communicated via `aria-activedescendant` rather than moving DOM focus,
// which is what lets Enter/Space/Arrow keys keep working straight off the
// button.
//
// 2026-08-10: 팝업만 radix `Popover` 로 옮겼다. 예전엔 `position: absolute` 로
// 떠서 스크롤 컨테이너(`.rd-wizard`)의 스크롤 높이에 그대로 더해졌고 —
// 목록을 펼치면 다이얼로그에 세로 스크롤바가 생기고 목록은 아래에서 잘렸다.
// Popover 는 body 로 portal 하고 공간이 모자라면 위로 뒤집으므로 둘 다 사라진다.
// 포커스는 계속 트리거에 머문다(`onOpenAutoFocus` 차단) — 위 aria-activedescendant
// 패턴이 그대로 성립해야 하기 때문.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDownIcon } from "../../wallet-info/components/icons";
import "../styles/component.css";

export interface DropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Optional muted second line under `label` ON THE CLOSED TRIGGER only
   * ("Network", "18 decimals", "on ONEchain", ...). Added by the 2026-07-30
   * redesign: chain and token became two half-width selectors on one row with
   * no field label of their own, so each has to say what it is. Deliberately
   * NOT rendered inside the popup list -- there the options are all the same
   * kind of thing, and a second line per row just makes the list harder to
   * scan. */
  subLabel?: string;
}

export interface DropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  id?: string;
}

export function Dropdown({ value, options, onChange, disabled, ariaLabel, id }: DropdownProps) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const isDisabled = Boolean(disabled) || options.length === 0;
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  // 바깥 클릭 닫기는 Popover 가 담당한다(예전 document pointerdown 리스너 제거).

  // Keep the highlighted option scrolled into view as it moves via keyboard.
  // Guarded: jsdom (used in tests) doesn't implement scrollIntoView at all.
  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  // 팝업은 body 로 portal 돼서 `.rd-root[data-theme]` 밖으로 나간다 — 열 때
  // 트리거가 서 있는 테마를 읽어 팝업에 그대로 찍어준다.
  const [popoverTheme, setPopoverTheme] = useState<string | null>(null);

  function openList() {
    if (isDisabled) return;
    setPopoverTheme(
      buttonRef.current?.closest("[data-theme]")?.getAttribute("data-theme") ?? null,
    );
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function closeList(refocus: boolean) {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }

  function commit(index: number) {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    closeList(true);
  }

  function handleButtonClick() {
    if (isDisabled) return;
    if (open) closeList(false);
    else openList();
  }

  function handleButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (isDisabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) openList();
        else commit(activeIndex);
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          closeList(true);
        }
        break;
      case "Tab":
        // Let Tab's default focus-move happen; just collapse the popup.
        if (open) setOpen(false);
        break;
      default:
        break;
    }
  }

  const activeOptionId = open && options[activeIndex] ? `${baseId}-option-${activeIndex}` : undefined;

  return (
    <Popover.Root open={open} onOpenChange={(next) => (next ? openList() : closeList(false))}>
      <div className="rd-dropdown">
        <Popover.Trigger asChild>
          <button
            type="button"
            id={baseId}
            ref={buttonRef}
            className="rd-dropdown-trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            aria-label={ariaLabel}
            disabled={isDisabled}
            onClick={handleButtonClick}
            onKeyDown={handleButtonKeyDown}
          >
            <span className="rd-dropdown-trigger-content">
              {selected?.icon}
              <span className="rd-dropdown-trigger-label">
                {selected?.label ?? ""}
                {selected?.subLabel && <small className="rd-dropdown-trigger-sub">{selected.subLabel}</small>}
              </span>
            </span>
            <span className="rd-dropdown-chevron" aria-hidden="true">
              <ChevronDownIcon size={16} />
            </span>
          </button>
        </Popover.Trigger>

      {open && (
        <Popover.Portal>
        <Popover.Content
          className="rd-dropdown-popover"
          data-theme={popoverTheme ?? undefined}
          side="bottom"
          align="start"
          sideOffset={5}
          collisionPadding={12}
          // 포커스는 트리거에 남는다 — 목록은 aria-activedescendant 로만 움직인다.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
        <ul
          className="rd-dropdown-listbox"
          role="listbox"
          id={listboxId}
          aria-label={ariaLabel}
          tabIndex={-1}
          data-vaul-no-drag
        >
          {options.map((opt, index) => {
            const isSelected = opt.value === value;
            const isActive = index === activeIndex;
            return (
              <li
                key={opt.value}
                id={`${baseId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                className={[
                  "rd-dropdown-option",
                  isActive ? "rd-dropdown-option-active" : "",
                  isSelected ? "rd-dropdown-option-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onPointerEnter={() => setActiveIndex(index)}
                // preventDefault: if a host wraps this dropdown in a <label>,
                // a plain click on this (non-interactive) <li> would trigger
                // the label's activation behavior -- a synthetic click
                // forwarded to the trigger button -- re-toggling the popup
                // open right after selection. Cancelling the default click
                // behavior suppresses that forwarding regardless of host
                // markup.
                onClick={(e) => {
                  e.preventDefault();
                  commit(index);
                }}
              >
                {opt.icon}
                <span className="rd-dropdown-option-label">{opt.label}</span>
                {isSelected && (
                  <span className="rd-dropdown-option-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        </Popover.Content>
        </Popover.Portal>
      )}
      </div>
    </Popover.Root>
  );
}
